<template>
  <div>
    <b-card>
      <b-row class="mb-2">
        <b-col cols="12">
          <h2 class="text-blue font-weight-bold mt-1">{{ $t('docreq_title') }}</h2>
          <p class="text-muted mb-0">
            {{ $t('docreq_subtitle') }}
          </p>
        </b-col>
      </b-row>

      <div class="recipient-section">
        <h4 class="mb-3 d-flex align-items-center">
          <feather-icon icon="UsersIcon" size="20" class="mr-1 text-primary"/>
          {{ $t('recipients') }}
        </h4>

        <b-row class="match-height">
          <b-col md="5" lg="4" class="d-flex flex-column">
            <EmailRecipientFilters
              :selected-roles.sync="selectedRolesObjects"
              :role-options="roleOptions"
              :search-query.sync="searchQuery"
              :user-count="users.length"
              :selected-count="selectedRecipients.length"
              :show-onboarding-filter="true"
              :onboarding-completed-only.sync="activeUsersOnly"
              :single-select="true"
              :hide-onboarding-when-role-open="false"
              @select-all="selectAllUsers"
              @deselect-all="deselectAllUsers"
            />
          </b-col>

          <b-col md="7" lg="8" class="d-flex flex-column">
            <EmailUserList
              :users="filteredUsers"
              :selected-recipients.sync="selectedRecipients"
              :filtered-users-count="filteredUsers.length"
              :search-query="searchQuery"
              :has-selected-roles="selectedRolesObjects.length > 0"
              :loading="loadingUsers"
            />
          </b-col>
        </b-row>
      </div>

      <b-row class="my-3" v-if="hasSelectedRole">
        <b-col cols="12" md="4">
          <b-form-group
              :label="$t('docreq_document_type')"
              label-class="font-weight-bold"
          >
            <b-form-select v-model="documentType" :options="availableDocumentTypeOptions" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="selectedDocumentExample" class="mb-3">
        <b-col cols="12">
          <b-card class="mb-0" bg-variant="light">
            <h5 class="mb-1">{{ selectedDocumentExample.title }}</h5>
            <p class="mb-0 text-muted">{{ selectedDocumentExample.example }}</p>
          </b-card>
        </b-col>
      </b-row>


      <div class="mt-3">
        <b-button
          variant="primary"
          :disabled="sending || selectedRecipients.length === 0 || !documentType"
          @click="sendRequests"
        >
          <b-spinner v-if="sending" small class="mr-1"/>
          {{ sending ? $t('sending') : $t('docreq_send_requests') }}
        </b-button>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import EmailRecipientFilters from "@/views/Email/components/EmailRecipientFilters.vue";
import EmailUserList from "@/views/Email/components/EmailUserList.vue";

export default {
  name: "DocumentRequests",
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BButton,
    BSpinner,
    EmailRecipientFilters,
    EmailUserList,
  },
  data() {
    return {
      loadingUsers: false,
      sending: false,
      documentType: "",
      activeUsersOnly: true,
      selectedRolesObjects: [],
      searchQuery: "",
      users: [],
      selectedRecipients: [],
      documentCatalog: [
        {
          value: "framework_agreement",
          textKey: "docreq_framework_role_based",
          allowedRoles: ["investors", "sellers"],
          titleKey: "docreq_framework_title",
          exampleKey: "docreq_framework_example",
        },
        {
          value: "risk_acknowledgement",
          textKey: "docreq_risk_investors",
          allowedRoles: ["investors"],
          titleKey: "docreq_risk_title",
          exampleKey: "docreq_risk_example",
        },
        {
          value: "investor_profile_agreement",
          textKey: "docreq_investor_profile_investors",
          allowedRoles: ["investors"],
          titleKey: "docreq_investor_profile_title",
          exampleKey: "docreq_investor_profile_example",
        },
        {
          value: "form_a",
          textKey: "docreq_form_a",
          allowedRoles: ["investors", "sellers"],
          titleKey: "docreq_form_a",
          exampleKey: "docreq_form_a_example",
        },
        {
          value: "form_k",
          textKey: "docreq_form_k",
          allowedRoles: ["investors", "sellers"],
          titleKey: "docreq_form_k",
          exampleKey: "docreq_form_k_example",
        },
      ],
      roleOptions: [
        {value: "investors", text: this.$t("investors")},
        {value: "sellers", text: this.$t("sellers")},
      ],
    };
  },
  computed: {
    hasSelectedRole() {
      return this.selectedRolesObjects.length > 0;
    },
    availableDocumentTypeOptions() {
      const selectedRoles = this.selectedRolesObjects.map(role => role.value);
      const hasSelection = selectedRoles.length > 0;

      const filtered = this.documentCatalog.filter(doc => {
        if (!hasSelection) return false;
        return selectedRoles.some(role => doc.allowedRoles.includes(role));
      });

      return [
        { value: "", text: this.$t('select_one') },
        ...filtered.map(doc => ({ value: doc.value, text: this.$t(doc.textKey) })),
      ];
    },
    selectedDocumentExample() {
      if (!this.documentType) return null;
      const doc = this.documentCatalog.find(item => item.value === this.documentType) || null;
      if (!doc) return null;
      return {
        title: this.$t(doc.titleKey),
        example: this.$t(doc.exampleKey),
      };
    },
    filteredUsers() {
      if (!this.searchQuery) return this.users;
      const q = this.searchQuery.toLowerCase();
      return this.users.filter(user =>
        user.full_name.toLowerCase().includes(q) || user.email.toLowerCase().includes(q)
      );
    },
  },
  watch: {
    selectedRolesObjects: {
      handler() {
        if (this.selectedRolesObjects.length > 1) {
          this.selectedRolesObjects = [this.selectedRolesObjects[0]];
          return;
        }
        if (!this.availableDocumentTypeOptions.some(option => option.value === this.documentType)) {
          this.documentType = "";
        }
        this.fetchUsers();
      },
      deep: true,
    },
    activeUsersOnly() {
      this.fetchUsers();
    },
  },
  methods: {
    async fetchUsers() {
      if (!this.selectedRolesObjects.length) {
        this.users = [];
        this.selectedRecipients = [];
        return;
      }

      this.loadingUsers = true;
      try {
        const roles = this.selectedRolesObjects.map(role => role.value);
        const response = await this.$http.get("/admin/emails/users", {
          params: { roles, active_only: this.activeUsersOnly },
        });
        this.users = response?.data?.value || [];

        const allowedIds = new Set(this.users.map(u => String(u.id)));
        this.selectedRecipients = this.selectedRecipients.filter(id => allowedIds.has(String(id)));
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: this.$t("failed_to_fetch_users"),
            icon: "AlertCircleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loadingUsers = false;
      }
    },
    selectAllUsers() {
      this.selectedRecipients = this.filteredUsers.map(user => user.id);
    },
    deselectAllUsers() {
      this.selectedRecipients = [];
    },
    async sendRequests() {
      this.sending = true;
      try {
        const response = await this.$http.post("/company/admin/document-requests/send", {
          document_type: this.documentType,
          recipients: this.selectedRecipients,
        });

        const result = response?.data?.value || {};
        const failed = result.failed || [];
        const reasonMap = {
          user_not_found: this.$t("docreq_reason_user_not_found"),
          company_connection_not_found: this.$t("docreq_reason_company_connection_not_found"),
          company_not_found: this.$t("docreq_reason_company_not_found"),
          missing_phone_number_or_country: this.$t("docreq_reason_missing_phone_or_country"),
          document_not_supported_for_user_role: this.$t("docreq_reason_document_not_supported_for_role"),
          signature_request_failed: this.$t("docreq_reason_signature_request_failed"),
          exception: this.$t("docreq_reason_exception"),
        };
        const failureSummary = failed
          .slice(0, 5)
          .map(item => {
            const userPart = item.user_id ? `#${item.user_id}` : this.$t("unknown");
            const reasonPart = reasonMap[item.reason] || item.reason || this.$t("unknown");
            const messagePart = item.message ? ` (${item.message})` : "";
            return `${userPart}: ${reasonPart}${messagePart}`;
          })
          .join(" | ");

        const baseText = `${this.$t('docreq_created')}: ${result.created_count || 0}, ${this.$t('docreq_failed')}: ${result.failed_count || 0}`;
        const detailsText = failed.length > 0
          ? `${baseText}. ${this.$t('docreq_failure_details')}: ${failureSummary}`
          : baseText;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success",
            text: detailsText,
            icon: failed.length > 0 ? "AlertTriangleIcon" : "CheckCircleIcon",
            variant: failed.length > 0 ? "warning" : "success",
          },
        });
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: error?.response?.data?.message || this.$t("docreq_failed_to_send"),
            icon: "AlertCircleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.text-blue {
  color: #345B48;
}
.recipient-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}
.match-height > [class*='col'] {
  display: flex;
  flex-flow: column;
}
</style>
